<template>
  <div class="desk-event-editor" @keyup.esc="close">
    <div class="content">
      <ul v-if="errorsRaw.length">
        <li class="error text-danger" v-for="(error, i) in errorsRaw" :key="i">
          {{ error.message }}
        </li>
      </ul>
      <div class="app-form-row">
        <i class="ion ion-md-contact row-icon"></i>
        <b-input-group v-if="clientEvent.employee_id" class="app-form-control mr-2">
          <b-input :value="employeeName" :class="{ 'is-invalid': errorFields.employee_id }" disabled />
          <template #append>
            <b-btn @click="employeeClear"><i class="ion ion-md-close"></i></b-btn>
          </template>
        </b-input-group>
        <AppSelect
          v-else
          class="app-form-control"
          @search="employeeSearch"
          @select="employeeSelect"
          @nextPage="employeeNextPage"
          :items="employees"
          :propose-new="false"
          :is-valid="!errorFields.employee_id"
          placeholder="select employee"
        >
          <template #default="props">
            <span
              @click="tryPreventSelectEmployee($event, props.item.id)"
              :class="[
                'select-item',
                {
                  owner: userEmployee && userEmployee.email === props.item.email,
                  busy: employeeBusyMap[props.item.id]
                }
              ]"
            >
              <app-avatar class="mr-2" v-if="props.item.avatar_id" :src="getAvatar(props.item)" />
              <span class="select-item-text">
                <div class="employee-name">{{ props.item.name }}</div>
                <div class="extra-title" v-if="getExtraTitle(props.item)" v-html="getExtraTitle(props.item)"></div>
                <span class="email">{{ props.item.email }}</span>
              </span>
            </span>
          </template>
        </AppSelect>
      </div>
      <div class="app-form-row" v-if="!infinityChecked">
        <i class="ion ion-md-time row-icon"></i>
        <app-date-select
          :is-valid="!errorFields.start"
          :date="clientEvent.start"
          class="app-form-control mr-1"
          @change="setStartDate"
          :marks-map="marksMap"
          :marks-format="marksFormat"
        />
        <app-time-select
          :is-valid="!errorFields.start"
          v-if="!allDayChecked"
          class="app-form-control-half"
          @change="setStartTime"
          :time="startTime"
        />
      </div>
      <div class="app-form-row" v-if="!infinityChecked">
        <div class="row-icon"></div>
        <app-date-select
          class="app-form-control mr-1"
          :is-valid="!errorFields.end"
          :date="clientEvent.end"
          :min-date="clientEvent.start"
          :marks-map="marksMap"
          :marks-format="marksFormat"
          :disabled="infinityChecked"
          :max-date="maxDate"
          @change="setEndDate"
        />
        <app-time-select
          v-if="!allDayChecked"
          class="app-form-control-half"
          :is-valid="!errorFields.end"
          :from-time="startTime"
          :time="endTime"
          :disabled="infinityChecked"
          @change="setEndTime"
        />
      </div>

      <div class="app-form-row my-3">
        <span class="mr-2"><i class="ion ion-md-infinite row-icon"></i></span>
        <div class="form-check">
          <input
            class="form-check-input"
            type="checkbox"
            id="infinityCheck"
            :checked="infinityChecked"
            :disabled="infinityDisabled"
            @change="infinityToggle"
          />
          <label class="form-check-label" for="infinityCheck">
            Infinity Booking
          </label>
        </div>
        <div class="form-check ml-auto" v-if="!infinityChecked">
          <input class="form-check-input" type="checkbox" id="allDayCheck" :checked="allDayChecked" @change="allDayToggle" />
          <label class="form-check-label" for="allDayCheck">
            All Day
          </label>
        </div>
      </div>
      <div class="app-form-row my-3" v-if="!infinityChecked && isLess24Hours">
        <span class="mr-2"><i class="ion ion-md-repeat row-icon"></i></span>
        <div class="form-check">
          <label class="form-check-label">
            <input class="form-check-input" type="checkbox" :checked="eventSet" @change="eventSetToggle" />
            Repeat at Same Time
          </label>
          <DeskEventSetEditor
            v-if="eventSet"
            :client-event="clientEvent"
            :event-set="eventSet"
            ref="eventSet"
            :limited="infinityDisabled"
          />
        </div>
      </div>

      <div class="app-form-row my-3">
        <span class="mr-2"><i class="ion ion-md-alarm row-icon"></i> Remind:</span>
        <div class="app-form-control">
          <app-simple-select :is-valid="!errorFields.remind" not-selected-title="Never" :options="remindOptions" @change="remindChange" />
        </div>
      </div>
      <div class="app-form-row mt-3 mb-0">
        <button class="btn btn-danger" v-if="!isNew" @click.prevent="destroy">Delete</button>
        <button class="btn btn-primary mr-3 ml-auto" @click.prevent="save">Save</button>
        <button class="btn btn-default" @click.prevent="close">Cancel</button>
      </div>
    </div>
    <desk-event-set-dialog v-if="showEventSetSaveDialog" @submit="handleSaveDialogAnswer" @close="showEventSetSaveDialog = false" />
    <desk-event-set-dialog
      v-if="showEventSetDeleteDialog"
      @submit="handleDeleteDialogAnswer"
      @close="showEventSetDeleteDialog = false"
      is-delete
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import AppSelect from '@/components/shared/AppSelect'
import AppDateSelect from '@/components/shared/AppDateSelect'
import AppTimeSelect from '@/components/shared/AppTimeSelect'
import { Api } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import AppSimpleSelect from '@/components/shared/AppSimpleSelect'
import { EMPLOYEE_LOAD } from '@/store/modules/employee'
import { SERVER_DATE_TIME_FORMAT } from '@/components/desk/utils'
import { isLocalId } from '@/utils/Id'
import AppAvatar from '@/components/shared/AppAvatar'
import { getAvatarSrc } from '@/utils/Helpers'
import DeskEventSetEditor from '@/components/desk/DeskEventSetEditor'
import {
  convertClientEventToServerEvent,
  convertServerEventToClient,
  EventSaveTypes,
  MAX_DAYS_DESK_BOOKING_LIMIT
} from '@/components/desk/helpers'
import { isEqual } from 'lodash'
import DeskEventSetDialog from '@/components/desk/DeskEventSetDialog'
import { MAP_UPDATE } from '@/store/modules/map'
import * as PN from '@/utils/PermissionNames'
import escape from 'lodash/escape'
import dayjs from 'dayjs'

const employeesApi = new Api(UrlStore.EMPLOYEES)
const deskEventSetsApi = new Api(UrlStore.DESK_EVENT_SETS)

export default {
  name: 'DeskEventEditor',
  components: {
    DeskEventSetDialog,
    DeskEventSetEditor,
    AppAvatar,
    AppSimpleSelect,
    AppTimeSelect,
    AppDateSelect,
    AppSelect
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    marksMap: {
      type: Map,
      default: () => {
        return new Map()
      }
    },
    marksFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    }
  },
  data: () => ({
    clientEvent: {},
    employees: [],
    allDayChecked: false,
    infinityChecked: false,
    userEmployee: null,
    errorFields: {},
    errorsRaw: [],
    saving: false,
    extraTitlesMap: new Map(),
    nextPageLoading: false,
    nextPagePromise: Promise.resolve(true),
    nextPage: 1,
    // map of employee statuses in selected period
    employeeBusyMap: {},
    employeeBusyMapTimeoutId: null,
    showEventSetSaveDialog: false,
    showEventSetDeleteDialog: false,
    eventSet: null,
    repeatEnabled: false
  }),
  computed: {
    ...mapState('auth', ['user']),
    ...mapGetters('rbac', ['can']),
    ...mapGetters('employee', ['getEmployee']),
    ...mapGetters('department', ['getDepartment']),
    ...mapGetters('point', ['getPoint']),
    ...mapGetters('desk', ['getDesk']),
    ...mapGetters('position', ['getPosition']),
    maxDate() {
      if (this.infinityDisabled) {
        return dayjs().add(MAX_DAYS_DESK_BOOKING_LIMIT, 'day')
      }
      return undefined
    },
    infinityDisabled() {
      return !this.can(PN.DESK_EVENT_CREATE_INFINITY)
    },
    isNew() {
      return this.event.id && isLocalId(this.event.id)
    },
    hasChanges() {
      return !isEqual(this.event, convertClientEventToServerEvent(this.clientEvent, this.infinityChecked))
    },
    remindOptions() {
      const activeValue = this.clientEvent.remind
      return [
        { value: null, title: 'Never', active: activeValue === null },
        { value: 0, title: 'At time of event', active: activeValue === 0 },
        { value: -5, title: '5 minutes before', active: activeValue === -5 },
        { value: -15, title: '15 minutes before', active: activeValue === -15 },
        { value: -30, title: '30 minutes before', active: activeValue === -30 },
        { value: -60, title: '1 hour before', active: activeValue === -60 },
        { value: -120, title: '2 hours before', active: activeValue === -120 },
        { value: -720, title: '12 hours before', active: activeValue === -720 },
        { value: -1440, title: '1 day before', active: activeValue === -1440 },
        { value: -10080, title: '1 week before', active: activeValue === -10080 }
      ]
    },
    startDate() {
      return this.clientEvent.key_ && this.clientEvent.start.format('L US')
    },
    startTime() {
      return this.clientEvent.key_ && this.clientEvent.start.format('HH:mm')
    },
    endDate() {
      return this.clientEvent.key_ && this.clientEvent.end.format('L US')
    },
    endTime() {
      return this.clientEvent.key_ && this.clientEvent.end.format('HH:mm')
    },
    employeeName() {
      if (this.clientEvent.employee_id) {
        const employee = this.getEmployee(this.clientEvent.employee_id)
        if (employee) return employee.name
      }

      return ''
    },
    isLess24Hours() {
      return this.clientEvent.end && this.clientEvent.end.diff(this.clientEvent.start, 'hours') < 24
    },
    needDeleteEventSetOnUpdate() {
      return this.clientEvent.set_id && this.eventSet === null
    }
  },
  methods: {
    ...mapActions('point', ['pointSave', 'pointLoad']),
    ...mapActions('desk', ['deskEventSave', 'deskEventDestroy', 'deskEventsSearch']),
    ...mapMutations('map', {
      mapUpdate_: MAP_UPDATE
    }),
    ...mapMutations('employee', {
      employeeLoad: EMPLOYEE_LOAD
    }),
    employeeClear() {
      this.clientEvent.employee_id = null
      this.employees = []
    },
    getAvatar(employee) {
      return getAvatarSrc(employee.avatar_id, employee.avatar_updated_at)
    },
    getExtraTitle(user) {
      if (!this.extraTitlesMap.has(user.id)) {
        let title = ''
        if (user.department_id) {
          const department = this.getDepartment(user.department_id)
          if (department) {
            title += `<div class="department">${escape(department.name)}</div>`
          }
        }

        if (user.position_id) {
          const position = this.getPosition(user.position_id)
          if (position) {
            title += `<div class="position">${escape(position.name)}</div>`
          }
        }

        this.extraTitlesMap.set(user.id, title)
      }

      return this.extraTitlesMap.get(user.id)
    },
    loadEmployeeBusyMap() {
      if (this.employeeBusyMapTimeoutId) {
        clearTimeout(this.employeeBusyMapTimeoutId)
      }
      this.employeeBusyMapTimeoutId = setTimeout(async () => {
        this.employeeBusyMap = {}
        if (this.clientEvent.start && this.clientEvent.end) {
          const response = await this.deskEventsSearch({
            params: {
              filters: {
                since: this.clientEvent.start.format(SERVER_DATE_TIME_FORMAT),
                until: this.clientEvent.end.format(SERVER_DATE_TIME_FORMAT)
              },
              pagerDisabled: true
            }
          })

          if (response && response.status === 200 && response.data.items.length) {
            response.data.items.forEach((deskEvent) => {
              if (this.user.rebooking_infinity_locked) {
                // every time slot blocking
                this.employeeBusyMap[deskEvent.employee_id] = true
              } else {
                // only not infinity blocking
                this.employeeBusyMap[deskEvent.employee_id] = deskEvent.end !== 'infinity'
              }
            })
          }
        }
      }, 600)
    },

    async save() {
      if (this.hasChanges || (this.$refs && this.$refs.eventSet.hasChanges)) {
        if (this.eventSet) {
          if (this.isNew) {
            await this.saveEventSet(EventSaveTypes.All)
          } else {
            this.showEventSetSaveDialog = true
          }
        } else {
          await this.saveEvent()
        }
      }
    },
    async handleSaveDialogAnswer(type) {
      this.showEventSetSaveDialog = false
      switch (type) {
        case EventSaveTypes.CurrentOnly:
          await this.saveEvent()
          break
        default:
          await this.saveEventSet(type)
      }
    },
    async saveEventSet(type) {
      if (this.saving) return
      try {
        this.saving = true
        await this.$refs.eventSet.save(type)
        this.close()
      } catch (error) {
        this.handleSaveError(error)
      } finally {
        this.saving = false
      }
    },
    async saveEvent() {
      if (this.saving) return
      try {
        this.saving = true
        if (this.needDeleteEventSetOnUpdate) {
          deskEventSetsApi.destroy({ id: this.clientEvent.set_id })
          this.clientEvent.set_id = null
        }
        const deskEvent = convertClientEventToServerEvent(this.clientEvent, this.infinityChecked)
        await this.deskEventSave({ deskEvent })
        this.errorFields = {}
        this.errorsRaw = []
        if (deskEvent.end === 'infinity') {
          await this.updatePoint()
        }
        this.close()
      } catch (error) {
        this.handleSaveError(error)
      } finally {
        this.saving = false
      }
    },
    handleSaveError(error) {
      this.saving = false
      if (error && error.response && error.response.status === 422) {
        error.response.data.forEach((error) => {
          this.$set(this.errorFields, error.field, true)
        })
        this.errorsRaw = error.response.data
      } else {
        throw error
      }
    },

    async destroy() {
      if (this.eventSet) {
        if (this.isNew) {
          await this.destroyEvent()
        } else {
          this.showEventSetDeleteDialog = true
        }
      } else {
        await this.destroyEvent()
      }
    },
    async handleDeleteDialogAnswer(type) {
      this.showEventSetDeleteDialog = false
      switch (type) {
        case EventSaveTypes.CurrentOnly:
          await this.destroyEvent()
          break
        default:
          await this.destroyEventSet(type)
      }
    },
    destroyEvent() {
      this.deskEventDestroy(this.event)
        .then(() => {
          this.$appToast.success('Booking deleted')
          this.close()
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 404) {
              this.$appToast.error('Booking not found!')
            } else if (error.response.status === 500) {
              this.$appToast.error('Internal server on booking delete!')
            }
          } else {
            throw error
          }
        })
    },
    async destroyEventSet(type) {
      await this.$refs.eventSet.destroy(type)
      this.close()
    },

    close() {
      this.$emit('close')
    },
    allDayToggle() {
      if (this.allDayChecked) {
        this.allDayChecked = false
      } else {
        this.allDayChecked = true
        this.clientEvent.start = this.clientEvent.start.set('hour', 0)
        this.clientEvent.start = this.clientEvent.start.set('minute', 0)
        this.clientEvent.end = this.clientEvent.end.set('hour', 23)
        this.clientEvent.end = this.clientEvent.end.set('minute', 59)
      }
      this.clientEvent.key_++
    },
    infinityToggle() {
      this.infinityChecked = !this.infinityChecked
    },
    employeeNextPage(term) {
      if (!this.nextPageLoading) {
        this.nextPageLoading = true
        const config = { params: { searchQuery: term, sort: 'name', page: ++this.nextPage } }
        this.nextPagePromise = employeesApi.getAll(config).then((r) => {
          if (r.data.pager.currentPage <= r.data.pager.pageCount) {
            this.nextPageLoading = false
          }
          if (this.userEmployee) {
            this.employees = [this.userEmployee, ...this.employees, ...r.data.items]
          } else {
            this.employees = [...this.employees, ...r.data.items]
          }
        })
      }
    },
    employeeSearch(term) {
      this.nextPage = 1
      const config = {
        params: {
          searchQuery: term,
          sort: 'name'
        }
      }
      employeesApi.getAll(config).then((r) => {
        if (this.userEmployee) {
          this.employees = [this.userEmployee, ...r.data.items]
        } else {
          this.employees = r.data.items
        }
      })
    },
    tryPreventSelectEmployee(event, employeeId) {
      if (this.employeeBusyMap[employeeId]) {
        event.stopPropagation()
      }
    },
    employeeSelect(employee) {
      this.employeeLoad(employee)
      this.clientEvent.employee_id = employee.id
    },
    userEmployeeLoad() {
      const config = { params: { email: this.user.email } }
      employeesApi.getAll(config).then((r) => {
        this.userEmployee = r.data.items[0]
      })
    },
    async loadCurrentEventSet() {
      const response = await deskEventSetsApi.get({ id: this.event.set_id })
      return response.data
    },
    async initEventSet() {
      this.eventSet = this.event.set_id ? await this.loadCurrentEventSet() : null
    },
    async eventSetToggle() {
      if (this.eventSet) {
        this.eventSet = null
      } else {
        this.eventSet = this.event.set_id ? await this.loadCurrentEventSet() : {}
      }
    },
    remindChange(selectedOption) {
      this.clientEvent.remind = selectedOption.value
    },
    setStartDate(date) {
      date = date.set('hour', this.clientEvent.start.get('hour'))
      date = date.set('minute', this.clientEvent.start.get('minute'))
      this.clientEvent.start = date
      const startEndDelta = this.clientEvent.end.diff(this.clientEvent.start, 'day')
      if (startEndDelta < 0) {
        this.clientEvent.end = date.add(0, 'day')
      }
      this.loadEmployeeBusyMap()
    },
    setEndDate(date) {
      date = date.set('hour', this.clientEvent.end.get('hour'))
      date = date.set('minute', this.clientEvent.end.get('minute'))
      this.clientEvent.end = date
      this.loadEmployeeBusyMap()
    },
    setStartTime(time) {
      this.clientEvent.start = this.clientEvent.start.set('hour', time.hour)
      this.clientEvent.start = this.clientEvent.start.set('minute', time.minute)
      this.clientEvent.key_++
      this.loadEmployeeBusyMap()
    },
    setEndTime(time) {
      this.clientEvent.end = this.clientEvent.end.set('hour', time.hour)
      this.clientEvent.end = this.clientEvent.end.set('minute', time.minute)
      this.clientEvent.key_++
      this.loadEmployeeBusyMap()
    },
    async updatePoint() {
      const desk = this.getDesk(this.event.desk_id)
      if (desk) {
        await this.pointLoad({ id: desk.point_id, params: { expand: 'employee' } })
      }
    }
  },
  created() {
    this.userEmployeeLoad()
  },
  watch: {
    event: {
      immediate: true,
      handler(event) {
        this.clientEvent = convertServerEventToClient(event)
      }
    },
    'event.set_id': {
      immediate: true,
      handler() {
        this.initEventSet()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.desk-event-editor {
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 66px);
  z-index: 1;
  overflow-y: scroll;
}

.select-item {
  &.busy {
    opacity: 0.6;
  }
}

.content {
  background: #fff;
  padding: 20px;
  margin: 0 20px;
  max-width: 100%;

  @media (max-width: 380px) {
    margin: 0;
  }
}

.row-icon {
  width: 22px;
  font-size: 22px;
  margin-right: 10px;
}

.app-form-row {
  display: flex;
  align-items: center;
  margin: 10px 0;
}

.app-form-control {
  flex: 2 1 50%;
}

.app-form-control-half {
  flex: 2 1 30%;
}

.email {
  display: block;
  font-size: 90%;
}

.owner {
  border-bottom: 1px solid #ccc;
}

.employee-name {
  font-weight: bold;
}

.extra-title {
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: clip;

  & > div {
    max-width: 100%;
  }
}
</style>
